import React, { useState } from 'react';
import {
  ChevronDownIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import type { InvestigationReportData } from '../types';
import { normalizeInvestigationReport } from '../utils/normalizeReport';
import {
  formatIstDateTime,
  hasValidSreTimestamp,
} from '../utils/formatLocalDateTime';
import { ensureSreFontsLoaded } from '../styles/theme';

const SEVERITY_STYLES: Record<string, string> = {
  critical: 'bg-red-50 text-red-800 ring-red-200',
  high: 'bg-orange-50 text-orange-800 ring-orange-200',
  medium: 'bg-amber-50 text-amber-900 ring-amber-200',
  low: 'bg-emerald-50 text-emerald-800 ring-emerald-200',
  info: 'bg-slate-100 text-slate-700 ring-slate-200',
};

const STATUS_STYLES: Record<string, string> = {
  resolved: 'bg-emerald-50 text-emerald-800 ring-emerald-200',
  mitigated: 'bg-teal-50 text-teal-800 ring-teal-200',
  ongoing: 'bg-primary-50 text-primary-800 ring-primary-200',
  inconclusive: 'bg-slate-100 text-slate-700 ring-slate-200',
};

const CONFIDENCE_STYLES: Record<string, string> = {
  confirmed: 'text-emerald-700 bg-emerald-50 ring-emerald-200',
  probable: 'text-amber-800 bg-secondary-100 ring-secondary-300',
  hypothesis: 'text-slate-600 bg-slate-100 ring-slate-200',
};

const PRIORITY_RAIL: Record<string, string> = {
  immediate: 'border-l-red-500',
  short_term: 'border-l-amber-500',
  long_term: 'border-l-primary-500',
  critical: 'border-l-red-500',
  high: 'border-l-orange-500',
  medium: 'border-l-amber-500',
  low: 'border-l-emerald-500',
};

function Chip({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <span
      className={[
        'inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ring-1 ring-inset',
        className,
      ].join(' ')}
    >
      {children}
    </span>
  );
}

function Section({
  index,
  title,
  children,
}: {
  index: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="border-t border-slate-100 px-5 py-4">
      <div className="mb-2.5 flex items-center gap-2.5">
        <span className="flex h-5 w-5 items-center justify-center rounded bg-primary-50 font-['IBM_Plex_Mono',ui-monospace,monospace] text-[10px] font-semibold text-primary-700">
          {index}
        </span>
        <h4 className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
          {title}
        </h4>
      </div>
      {children}
    </section>
  );
}

export interface InvestigationReportProps {
  report: InvestigationReportData;
  className?: string;
}

/**
 * Structured investigation report — Pulse workbench identity (not OpenSRE TraceViewer styling).
 */
const InvestigationReport: React.FC<InvestigationReportProps> = ({
  report,
  className = '',
}) => {
  const [timelineExpanded, setTimelineExpanded] = useState(false);
  React.useEffect(() => {
    ensureSreFontsLoaded();
  }, []);

  const normalized = normalizeInvestigationReport(
    report as InvestigationReportData & Record<string, unknown>
  );

  const sev = String(normalized.severity || 'info').toLowerCase();
  const statusKey = String(normalized.status || '').toLowerCase();
  const timeline = (Array.isArray(normalized.timeline) ? normalized.timeline : []).filter(
    (entry) => hasValidSreTimestamp(entry.time)
  );
  const showTimelineToggle = timeline.length > 5;
  const visibleTimeline = timelineExpanded ? timeline : timeline.slice(0, 5);

  let section = 1;
  const next = () => String(section++).padStart(2, '0');

  return (
    <article
      className={[
        'sre-wb-fade overflow-hidden rounded-xl border border-slate-200 bg-white shadow-[0_1px_0_rgba(15,23,42,0.04)]',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <header className="relative overflow-hidden border-b border-slate-100 bg-gradient-to-br from-primary-50 via-white to-secondary-50 px-5 py-4">
        <div className="absolute inset-y-0 left-0 w-1 bg-primary-600" />
        <div className="flex flex-wrap items-start justify-between gap-3 pl-2">
          <div className="min-w-0">
            <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-primary-600">
              Investigation report
            </p>
            <h3 className="text-lg font-semibold leading-snug tracking-tight text-slate-900">
              {normalized.title}
            </h3>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <Chip className={SEVERITY_STYLES[sev] || SEVERITY_STYLES.info}>{sev}</Chip>
            {normalized.status && (
              <Chip className={STATUS_STYLES[statusKey] || STATUS_STYLES.inconclusive}>
                {String(normalized.status).replace(/_/g, ' ')}
              </Chip>
            )}
          </div>
        </div>
        {normalized.affected_services && normalized.affected_services.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5 pl-2">
            {normalized.affected_services.map((svc) => (
              <span
                key={svc}
                className="rounded border border-slate-200 bg-white/80 px-2 py-0.5 font-['IBM_Plex_Mono',ui-monospace,monospace] text-[11px] text-slate-600"
              >
                {svc}
              </span>
            ))}
          </div>
        )}
      </header>

      {normalized.executive_summary && (
        <Section index={next()} title="Executive summary">
          <p className="text-sm leading-relaxed text-slate-700">
            {normalized.executive_summary}
          </p>
        </Section>
      )}

      {normalized.impact &&
        (normalized.impact.user_facing ||
          normalized.impact.service_impact ||
          normalized.impact.blast_radius) && (
          <Section index={next()} title="Impact">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              {[
                { label: 'User-facing', value: normalized.impact.user_facing },
                { label: 'Service', value: normalized.impact.service_impact },
                { label: 'Blast radius', value: normalized.impact.blast_radius },
              ]
                .filter((c) => c.value)
                .map((c) => (
                  <div
                    key={c.label}
                    className="rounded-lg border border-slate-100 bg-slate-50/80 px-3 py-2.5"
                  >
                    <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                      {c.label}
                    </div>
                    <div className="text-sm leading-snug text-slate-700">{c.value}</div>
                  </div>
                ))}
            </div>
          </Section>
        )}

      {timeline.length > 0 && (
        <Section index={next()} title="Timeline">
          <ol className="relative space-y-0 border-l border-slate-200 ml-1.5">
            {visibleTimeline.map((entry, i) => (
              <li key={`${entry.time}-${i}`} className="relative pb-3 pl-4 last:pb-0">
                <span className="absolute -left-[5px] top-1.5 h-2.5 w-2.5 rounded-full border-2 border-primary-500 bg-white" />
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                  <span className="font-['IBM_Plex_Mono',ui-monospace,monospace] text-xs font-medium text-primary-700">
                    {formatIstDateTime(entry.time)}
                  </span>
                  <span className="text-sm text-slate-700">{entry.event}</span>
                </div>
              </li>
            ))}
          </ol>
          {showTimelineToggle && (
            <button
              type="button"
              onClick={() => setTimelineExpanded((v) => !v)}
              className="mt-2 ml-1.5 inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-800"
            >
              {timelineExpanded ? (
                <>
                  <ChevronDownIcon className="h-3.5 w-3.5" /> Collapse timeline
                </>
              ) : (
                <>
                  <ChevronRightIcon className="h-3.5 w-3.5" /> Show {timeline.length - 5} more
                </>
              )}
            </button>
          )}
        </Section>
      )}

      {normalized.root_cause?.summary && (
        <Section index={next()} title="Root cause">
          <div className="rounded-lg border border-secondary-200 bg-gradient-to-br from-secondary-50 to-white px-3.5 py-3">
            {normalized.root_cause.confidence && (
              <span
                className={`mb-2 inline-flex rounded-md px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ring-1 ring-inset ${
                  CONFIDENCE_STYLES[String(normalized.root_cause.confidence)] ||
                  CONFIDENCE_STYLES.hypothesis
                }`}
              >
                {normalized.root_cause.confidence}
              </span>
            )}
            <p className="text-sm font-medium leading-relaxed text-slate-900">
              {normalized.root_cause.summary}
            </p>
            {normalized.root_cause.details && (
              <p className="mt-2 text-sm leading-relaxed text-slate-600">
                {normalized.root_cause.details}
              </p>
            )}
          </div>
        </Section>
      )}

      {normalized.action_items && normalized.action_items.length > 0 && (
        <Section index={next()} title="Recommended actions">
          <ul className="space-y-2">
            {normalized.action_items.map((item, i) => (
              <li
                key={`${item.action}-${i}`}
                className={`rounded-r-lg border border-slate-100 border-l-[3px] bg-slate-50/60 px-3 py-2 ${
                  PRIORITY_RAIL[String(item.priority)] || 'border-l-slate-300'
                }`}
              >
                <span className="mb-0.5 block text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                  {String(item.priority).replace(/_/g, ' ')}
                </span>
                <span className="text-sm text-slate-800">{item.action}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {normalized.lessons_learned && normalized.lessons_learned.length > 0 && (
        <Section index={next()} title="Lessons learned">
          <ul className="space-y-1.5">
            {normalized.lessons_learned.map((lesson, i) => (
              <li key={`${lesson}-${i}`} className="flex gap-2 text-sm text-slate-700">
                <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-primary-500" />
                <span className="leading-relaxed">{lesson}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}
    </article>
  );
};

export default InvestigationReport;
